import { css } from '@emotion/core'
import { graphql } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'
import Helmet from 'react-helmet'

import AvatarIntro from '../components/AvatarIntro'
import Block from '../components/Block'
import Body from '../components/Body'
import CTAEbook from '../components/ctas/CTAEbook'
import Divider from '../components/Divider'
import { FAQ, FAQContainer, FAQdata } from '../components/FAQ'
import Footer from '../components/Footer'
import Header from '../components/Header'
import Layout from '../components/Layout'
import { ProductDigestSignup } from '../components/ProductDigestSignup'
import { ProductHeroCard } from '../components/ProductHeroCard'
import { ProductHeroResource } from '../components/ProductHeroResource'
import ReadingProgressBar from '../components/ReadingProgressBar'
import RichText from '../components/RichText'
import SEO from '../components/SEO'
import { breakpoints } from '../constants/breakPoints'
import { richTextRenderingOptions } from '../helpers/contentful'
import { ProductHeroProps } from '../interfaces/product-heroes'

export const ProductLeaderPageTemplate: React.FunctionComponent<ProductHeroProps> = ({ data }) => {
  const { productHero, productHeroes } = data
  return (
    <Layout blackBG>
      {!!productHero.frequentlyAskedQuestions && productHero.frequentlyAskedQuestions.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {` 
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [${FAQdata(productHero.frequentlyAskedQuestions)}]
              }
            `}
          </script>
        </Helmet>
      )}
      <SEO
        title={productHero.seoTitle}
        withTitleTemplate={false}
        description={productHero.seoAbstract}
        imageSrc={`https:${productHero.seoImage.file.url}`}
        lang="en"
      />
      <Header hideOnScroll />
      <Body>
        <ReadingProgressBar />
        <AvatarIntro
          blackBG
          name={productHero.name}
          companyName={productHero.company}
          jobTitle={productHero.personalTitle}
          authorPhoto={productHero.seoImage}
          description={productHero.abstract}
          socialLinks={productHero.links}
        />

        <h2 className="heading2 blue-mesh-text center">{productHero.contentTitle}</h2>

        <Block columns={2} columnsTemplate="3fr 8fr" alignContent="flex-start" css={blockStyle}>
          <div className="hide-on-small-medium" css={adCardStyle}>
            <CTAEbook isSidebarCta />
          </div>

          <div>
            <div>
              <RichText>{renderRichText(productHero.content, richTextRenderingOptions)}</RichText>
              {!!productHero.frequentlyAskedQuestions && productHero.frequentlyAskedQuestions.length > 0 && (
                <>
                  <Divider size={6} color="rgba(136, 146, 158, 0.4)" notDecreased />
                  <h3 className="heading3">General FAQ</h3>
                  <div className="rich-text" css={[FAQContainer, FAQStyle]}>
                    {productHero.frequentlyAskedQuestions.map(
                      ({ question: { question }, answer: { answer } }, index) => (
                        <FAQ blackBG withBlueChevron key={index} question={question} answer={answer} />
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Block>

        {productHero.resources && (
          <>
            <Divider size={8} color="rgba(136, 146, 158, 0.4)" />
            <h2 className="heading2 blue-mesh-text center">Curated resources from {productHero.name}</h2>
            <Block columns={3} twoColumnsForTablet breakPoint={breakpoints.large}>
              {productHero.resources.map((resource, index) => (
                <ProductHeroResource
                  key={index}
                  imageSrc={resource.image}
                  imgAlt={resource.image.title}
                  title={resource.title}
                  tags={resource.tags}
                  link={resource.link}
                />
              ))}
            </Block>
          </>
        )}

        <Divider size={8} color="rgba(136, 146, 158, 0.4)" />

        <h2 className="heading2 blue-mesh-text center">You might also be interested to see</h2>
        <Block columns={4} noBreakForTablet css={alsoInterestedBlockStyle}>
          {productHeroes.edges
            .filter(({ node }) => node.slug !== productHero.slug)
            .slice(0, 4)
            .map(({ node: productHero }) => (
              <ProductHeroCard
                key={productHero.slug}
                imageSrc={productHero.profilePicture}
                company={productHero.company}
                companyLogo={productHero.companyLogo}
                name={productHero.name}
                title={productHero.personalTitle}
                slug={productHero.slug}
              />
            ))}
        </Block>

        <Divider size={8} color="rgba(136, 146, 158, 0.4)" />

        <ProductDigestSignup />
      </Body>
      <Footer blackBG />
    </Layout>
  )
}

export default ProductLeaderPageTemplate

export const query = graphql`
  query ($id: String!) {
    productHero: contentfulProductHero(id: { eq: $id }) {
      name
      personalTitle
      abstract
      seoAbstract
      seoTitle
      slug
      contentTitle
      resources {
        title
        link
        tags {
          name
        }
        image {
          title
          gatsbyImageData(width: 92, height: 92, quality: 90)
        }
      }
      seoImage {
        gatsbyImageData(width: 128, height: 128, quality: 90)
        file {
          url
        }
      }
      content {
        raw
      }
      links {
        title
        url
      }
      company
      frequentlyAskedQuestions {
        question {
          question
        }
        answer {
          answer
        }
      }
    }
    productHeroes: allContentfulProductHero(limit: 5) {
      edges {
        node {
          name
          personalTitle
          slug
          company
          companyLogo {
            file {
              url
            }
          }
          profilePicture {
            gatsbyImageData(width: 400, quality: 90)
          }
        }
      }
    }
  }
`

const blockStyle = css`
  column-gap: calc(100% / 12 + 1rem) !important;
`

const adCardStyle = css`
  position: sticky;
  top: 5.5rem;
`

export const FAQStyle = css`
  width: 100%;
  margin-top: 2rem;
`

const alsoInterestedBlockStyle = css`
  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: repeat(3, 1fr);

    a:last-child {
      display: none;
    }
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: initial;
  }
`
